import React from 'react';
import { Cross1Icon } from '@radix-ui/react-icons';
import { FilePlus1Icon } from 'icons';
import { RadioButton, ModalWindow, Loader } from 'components';
import {
  ButtonAddImage,
  ButtonCustom,
  CellModal,
  ContainerModal,
  ContentDate,
  DateLabel,
  InputModal,
  LabelModal,
  ModalTitle,
  PdfDownloadButton,
  PdfListItem,
  StyledCalendarIcon,
  Overlay,
} from './styles';
import NewCarousel from '~/components/NewCarousel';
import { Flex } from 'reflexbox';
import { SingleDatePicker } from 'react-dates';
import { colors } from 'styles/colors';
import { useTranslation } from 'react-i18next';

interface FeedbackModalProps {
  isLoading: boolean;
  state: any;
  maintenance: boolean | null;
  setMaintenance: (value: boolean) => void;
  images: Array<{ preview: string }>;
  pdfs: Array<{ preview: string; name: string }>;
  handleModalClose: () => void;
  handleImageChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  removeImage: (index: number) => void;
  removePdf: (index: number) => void;
  formatPhone: (value: string) => string;
  submitFeedback: () => void;
  setState: (value: any) => void;
  render: () => void;
}

export function FeedbackModal({
  isLoading,
  state,
  maintenance,
  setMaintenance,
  images,
  pdfs,
  handleModalClose,
  handleImageChange,
  removeImage,
  removePdf,
  formatPhone,
  submitFeedback,
  setState,
  render,
}: Readonly<FeedbackModalProps>): JSX.Element {
  const { t } = useTranslation();
  const isDesktop = window.matchMedia('(min-width: 1024px)').matches;

  const downloadPdf = async (url: string, name: string) => {
    const filename = `${name.replace(/\.[^.]+$/, '')}.pdf`;
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(url, '_blank');
    }
  };

  return (
    <ModalWindow
      borderTop={isDesktop}
      style={{
        width: isDesktop ? '500px' : '80%',
        padding: isDesktop ? '10px' : '0px',
      }}
      onClickOutside={handleModalClose}
    >
      <ContainerModal style={{ padding: isDesktop ? '10px' : '20px' }}>
        {isLoading && (
          <Overlay>
            <Flex style={{ display: 'flex', flexDirection: 'column' }}>
              <Loader />
              {
                  state.feedbackInfo ? t('carregando') : t('enviandoInformacoes')
                }
            </Flex>
          </Overlay>
        )}
        <Flex flexDirection="column">
          {/* Header */}
          <Flex justifyContent="space-between" alignItems="center" mb="20px">
            <ModalTitle>
              {state.feedbackInfo ? t('verInformacoes') : t('adicionarInformacoes')}
            </ModalTitle>
            <Cross1Icon
              style={{ cursor: 'pointer' }}
              onClick={handleModalClose}
              color={colors.Grey400}
              width={20}
              height={20}
            />
          </Flex>

          {/* Motivo */}
          <CellModal>
            <Flex>
              <LabelModal>
                {t('porqueGostariaDeComplementarEsteLaudo?')}
                <span style={{ color: colors.Grey400 }}>
                  {` (${t('obrigatorio')})`}
                </span>
              </LabelModal>
            </Flex>
            <InputModal
              disabled={!!state.feedbackInfo}
              type="text"
              placeholder="Digite o motivo"
              value={state.reason}
              onChange={(e) => setState({ reason: e.target.value })}
            />
          </CellModal>

          {/* Telefone */}
          <br />
          <CellModal>
            <Flex>
              <LabelModal>
                {t('insiraUmTelefoneParaContato')}
                <span style={{ color: colors.Grey400 }}>
                  {` (${t('opcional')})`}
                </span>
              </LabelModal>
            </Flex>
            <InputModal
              type="tel"
              disabled={!!state.feedbackInfo}
              placeholder="(00) 00000-0000"
              value={state.phone}
              onChange={(e) => setState({ phone: formatPhone(e.target.value) })}
            />
          </CellModal>

          {/* Manutenção */}
          <br />
          <CellModal>
            <Flex>
              <LabelModal>
                {t('foiRealizadoManutencaoNoEquipamento')}
                <span style={{ color: colors.Grey400 }}>
                  {` (${t('opcional')})`}
                </span>
              </LabelModal>
            </Flex>
            <Flex>
              <RadioButton
                label={t('nao')}
                disabled={!!state.feedbackInfo}
                name="maintenance"
                style={{ marginTop: '5px' }}
                checked={maintenance === false}
                onClick={() => { !state.feedbackInfo && setMaintenance(false); }}
              />
              <RadioButton
                label={t('sim')}
                disabled={!!state.feedbackInfo}
                name="maintenance"
                style={{ marginTop: '5px', marginLeft: '20px' }}
                checked={maintenance === true}
                onClick={() => { !state.feedbackInfo && setMaintenance(true); }}
              />
            </Flex>
          </CellModal>

          {/* Data da manutenção */}
          {maintenance && (
            <>
              <br />
              <CellModal>
                <Flex>
                  <LabelModal>
                    {t('quandoFoiRealizadoAManutencaoDoEquipamento')}
                    <span style={{ color: colors.Grey400 }}>
                      {` (${t('opcional')})`}
                    </span>
                  </LabelModal>
                </Flex>
                <ContentDate>
                  <DateLabel>{t('data')}</DateLabel>
                  <SingleDatePicker
                    disabled={!!state.feedbackInfo}
                    date={state.selectedDate}
                    focused={state.isFocusedDate}
                    onFocusChange={({ focused }) => setState({ isFocusedDate: focused })}
                    onDateChange={(date) => {
                      state.selectedDate = date;
                      render();
                    }}
                    numberOfMonths={1}
                    displayFormat="DD/MM/YYYY"
                    isOutsideRange={(d) => !d.isBefore(state.tomorrow)}
                    placeholder={t('dd/mm/aaaa')}
                  />
                  <StyledCalendarIcon color="#202370" />
                </ContentDate>
              </CellModal>
            </>
          )}

          <br />
        </Flex>

        {/* Upload de imagens */}
        <LabelModal>
          {t('adicionarImagensDasMedicoesNoEquipamento')}
          <span style={{ color: colors.Grey400 }}>
            {` (${t('opcional')})`}
          </span>
        </LabelModal>

        {images.length > 0 && (
          <>
            <br />
            <NewCarousel
              images={images.map((img) => img.preview)}
              showViewMode={!state.feedbackInfo}
              onDelete={removeImage}
            />
          </>
        )}

        {pdfs.length > 0 && (
          <Flex flexDirection="column" mt="8px">
            {pdfs.map((pdf, index) => (
              <PdfListItem key={pdf.preview}>
                <PdfDownloadButton type="button" onClick={() => downloadPdf(pdf.preview, pdf.name)}>
                  {pdf.name}
                </PdfDownloadButton>
                {!state.feedbackInfo && (
                  <Cross1Icon
                    style={{ cursor: 'pointer', flexShrink: 0, marginLeft: '8px' }}
                    onClick={() => removePdf(index)}
                    color={colors.Grey400}
                    width={14}
                    height={14}
                  />
                )}
              </PdfListItem>
            ))}
          </Flex>
        )}

        <br />

        {/* Footer */}
        {state.feedbackInfo ? (
          <Flex
            style={{
              width: '100%',
              justifyContent: 'center',
              marginTop: '20px',
            }}
          >
            <ButtonCustom
              style={{
                backgroundColor: 'transparent',
                color: colors.Grey300,
                boxShadow: 'none',
                fontWeight: 'normal',
                border: '0px none',
              }}
              onClick={handleModalClose}
            >
              {t('fechar')}
            </ButtonCustom>
          </Flex>
        ) : (
          <>
            <ButtonAddImage
              htmlFor="imageUpload"
              style={{
                height: '41px',
                textTransform: 'capitalize',
                borderRadius: '4px',
                margin: '10px 0px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <FilePlus1Icon size={18} />
              {`${t('adicionarImagens')} / PDF`}
            </ButtonAddImage>

            <input
              id="imageUpload"
              multiple
              accept="image/*,.pdf,application/pdf"
              onChange={handleImageChange}
              style={{ display: 'none' }}
              type="file"
            />

            <Flex
              style={{
                width: '100%',
                justifyContent: 'space-between',
                marginTop: '20px',
              }}
            >
              <ButtonCustom
                style={{
                  backgroundColor: 'transparent',
                  color: colors.Grey300,
                  boxShadow: 'none',
                  fontWeight: 'normal',
                  border: '0px none',
                }}
                onClick={handleModalClose}
              >
                {t('cancelar')}
              </ButtonCustom>
              <ButtonCustom
                style={{
                  height: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={submitFeedback}
              >
                {t('enviar')}
              </ButtonCustom>
            </Flex>
          </>
        )}
      </ContainerModal>
    </ModalWindow>
  );
}
